import { NaniumObject, Type } from "nanium/objects";
import { AddressType } from "./types/address.type";
import { OpeningHoursType } from "./types/openingHours.type";
import { GeoLocationType } from "./types/geoLocation.type";
import {ClubFeatures} from "./types/facility/ClubFeatures";
import {ConsultingTag} from "./types/facility/ConsultingTag";

export class Facility extends NaniumObject<Facility> {
  static strictDefault: boolean = true;

  @Type(String) id?: string;
  @Type(String) name?: string;
  @Type(String) description?: string;

  @Type(AddressType) address?: AddressType;
  @Type(GeoLocationType) location?: GeoLocationType;
  // True when location was hand-placed on a map (web-app picker) instead of
  // geocoded from the address. Every automatic geocoding path must leave such
  // a location untouched; only an explicit user action may clear the flag.
  @Type(Boolean) locationManuallySet?: boolean;

  @Type(OpeningHoursType) openingHours?: OpeningHoursType;
  @Type(String) openingHoursUnformatted?: string;
  @Type(String) phoneNumber?: string;
  @Type(String) contactMail?: string;
  @Type(String) carrier?: string;
  @Type(String) website?: string;

  @Type(Array, String) pictures?: string;
  @Type(String) titlePicture?: string;
  @Type(String) logo?: string;
  @Type(Array, String) videos?: string[];

  // CLUBS
  @Type(Boolean) isClub?: boolean;
  @Type(ClubFeatures) clubFeatures?: ClubFeatures;
  @Type(Array, String) participationOffers?: string[];

  // CONSULTING FACILITIES
  @Type(Boolean) isConsulting?: boolean;
  @Type(ConsultingTag) consultingTags?: ConsultingTag;
  @Type(Array, String) consultingOffers?: string[];

  // OTHER OFFERS
  @Type(Boolean) isOther?: boolean;
  @Type(String) otherOffer?: string;

  // TEMPORARY
  @Type(Number) distanceFromUser?: number;

  @Type(Number) viewsLast14Days?: number;
  @Type(Number) viewsPrevious14Days?: number;
  @Type(Number) viewsLast365Days?: number;

  // True when the facility has not yet been classified into one of the new
  // facility-type buckets, or when the type-specific sub-properties are still
  // empty. Drives the facility-onboarding wizard and its re-entry prompts.
  //
  // Beratung facilities (isConsulting) are intentionally exempt: nothing about
  // the new structures is relevant to them, so they are never forced into the
  // wizard nor shown "missing config" prompts. They are merely informed about
  // the new categories elsewhere (see onboarding-notice consulting info).
  public static needsOnboarding(facility: Facility): boolean {
    return Facility.onboardingReason(facility) !== null;
  }

  // Why a facility still needs onboarding, or null when it is complete/exempt.
  // needsOnboarding() is defined in terms of this so the admin overview's
  // "what's missing" label can never drift from the gate that drives the wizard.
  // Returned codes: 'unclassified' | 'clubWithoutFeatures' | 'otherWithoutOffer'.
  public static onboardingReason(facility: Facility): string | null {
    if (!facility) {
      return null;
    }
    const hasType = !!(facility.isClub || facility.isConsulting || facility.isOther);
    if (!hasType) {
      // Unclassified facility — must still be classified (hottest candidate
      // for the new "anderes Angebot" category).
      return 'unclassified';
    }
    // Checked before the club case so dirty multi-flag legacy data always errs
    // toward exemption — never tell a Beratung facility to add clubFeatures.
    if (facility.isConsulting) {
      return null;
    }
    if (facility.isClub && !Facility.hasAnyClubFeature(facility.clubFeatures)) {
      return 'clubWithoutFeatures';
    }
    if (facility.isOther && !facility.otherOffer) {
      return 'otherWithoutOffer';
    }
    return null;
  }

  // Public so the web-app update form can reuse the exact same "is there at
  // least one club feature" rule when enforcing classification in onboarding
  // contexts — keeping the form's gate and needsOnboarding from drifting apart.
  public static hasAnyClubFeature(features?: ClubFeatures): boolean {
    if (!features) {
      return false;
    }
    return Object.values(features).some(v => v === true || (typeof v === 'string' && v.length > 0));
  }
}
